import {
    Component,
    ViewEncapsulation,
    ChangeDetectionStrategy,
    Input,
    ChangeDetectorRef,
    ElementRef,
    EventEmitter,
    Output,
    OnChanges,
    SimpleChanges,
    OnInit,
    ViewChild,
    AfterViewInit,
    inject,
    DestroyRef,
    Signal,
    signal,
    OnDestroy,
} from '@angular/core';
import { toObservable, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { distinctUntilChanged, filter } from 'rxjs';

import { SearchInputFileConfig } from './interfaces/search-input-file-config.interface';
import { FileState } from '../../../interfaces/file-state.interface';
import { UploadFile } from '../../../interfaces/file-upload.interface';
import { FileStateService } from '../../../services/file-state.service';
import { UploadService } from '../../../services/upload-progress.service';
import { FileUploadConfig } from '../../constants/file-config.constant';
import LoaderService from '../../loading.service';
import { areFileArraysEqual } from '../../utils';
import { ActionModalComponent } from '../action-modal/action-modal.component';
import { ActionModalButton } from '../action-modal/action-modal.interface';
import { ToastOutletService } from '../toast-outlet/toast-outlet.service';

@Component({
    selector: 'mapp-mercer-mind-search-input',
    templateUrl: './mercer-mind-search-input.component.html',
    styleUrls: ['./mercer-mind-search-input.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class MerecerMindSearchInputComponent
    implements OnChanges, OnInit, AfterViewInit, OnDestroy
{
    private readonly maxVisibleLines = 3;

    @Input() searchValue: { text: string } | null;

    @Input() readOnlyInput = false;

    @Input() placeholder: string;

    @Input() disabled: boolean;

    @Output() cancelUpload = new EventEmitter<void>();

    @Output() emitChat = new EventEmitter<string>();

    @Output() emitPause = new EventEmitter<boolean>();

    @Output() emitReset = new EventEmitter<boolean>();

    @Output() filesSelected = new EventEmitter<FileState[]>();

    @Input() conversationfiles: Signal<UploadFile[]> = signal([]);

    @ViewChild('fileModal') fileModal!: ActionModalComponent;
    protected primaryButtonFileObject: ActionModalButton = {
        label: 'Save File',
        action: () => {
            this.progressService.setSelectedFileTemporaryConsent(false);
            this.openFileUploadFromModal();
        },
    };

    protected secondaryNoButtonFileObject: ActionModalButton = {
        label: 'Upload Only',
        action: () => {
            this.progressService.setSelectedFileTemporaryConsent(true);
            this.openFileUploadFromModal();
        },
    };

    protected titleFileModal =
        'Would you like to save this file securely in Aida for future chats?';
    protected bodyFileModal =
        "Saved files will be stored in compliance with TAAP and Aida's privacy and security policies. You can delete your saved files anytime.";

    @ViewChild('fileWarningModal') fileWarningModal!: ActionModalComponent;

    @ViewChild('chatInput') inputElementRef!: ElementRef<HTMLTextAreaElement>;

    private readonly textareaMaxLength = 10000;

    private destroyRef = inject(DestroyRef);

    progressService = inject(UploadService);

    fileUploadConfig: SearchInputFileConfig = FileUploadConfig;

    showFilters = false;

    valueText: string;

    isTyping = true;

    textareaRows = 1;

    textArea: HTMLTextAreaElement;
    hasPageScroll = false;
    textareaHasOverflow = false;

    files: FileState[] = [];

    disableSubmit: boolean;

    fileWarningMessage = '';
    fileWarningTitle = 'File Upload Error';
    selectedFile: File | null = null;

    /**
     * True only on the "/aida" landing page — the one place the attachment
     *  icon is allowed to be shown. Kept in sync with router navigation.
     */
    protected isLandingPage = signal(false);

    /**
     *
     * @param ref
     * @param elementRef
     * @param router
     * @param loaderService
     * @param location
     * @param fileStateService
     * @param toastOutletService
     */
    constructor(
        private readonly ref: ChangeDetectorRef,
        private readonly elementRef: ElementRef,
        private readonly router: Router,
        public loaderService: LoaderService,
        public fileStateService: FileStateService,
        private toastOutletService: ToastOutletService
    ) {
        toObservable(this.progressService._selectedFile)
            .pipe(
                distinctUntilChanged(),
                filter((file) => !!file),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((file) => {
                if (file.name === this.progressService.selectedFileName) {
                    return;
                }
                this.selectedFile = this.progressService.selectedFileValue;
                this.progressService.setFileName(file.name || '');
                this.handleSelectedFile();
            });

        toObservable(this.fileStateService.files)
            .pipe(distinctUntilChanged(areFileArraysEqual), takeUntilDestroyed(this.destroyRef))
            .subscribe((files) => {
                // Change condition to avoid infinite loop

                if (files.length !== this.files.length) {
                    this.files = [...files];
                    this.ref.detectChanges();
                }
            });

        // The attachment/paperclip icon must only ever be offered on the fresh
        // "/aida" landing page, never in an active conversation ("/aida/chat")
        // or anywhere else. `currentAttachmentDisabledFlag` alone isn't a tight
        // enough guard (it can legitimately be false during an active
        // conversation), so track the route explicitly as a second, independent
        // check.
        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((event: NavigationEnd) => {
                this.isLandingPage.set(this.computeIsLandingPage(event.urlAfterRedirects));
            });
    }
    /**
     *
     */
    ngOnDestroy(): void {
        this.progressService.cleanup();
        window.removeEventListener('resize', this.boundCheckPageScroll);
        window.removeEventListener('scroll', this.boundCheckPageScroll);
    }

    /**
     *
     */
    get textareaText(): string {
        return this.textArea?.value ?? '';
    }

    /**
     *
     */
    set textareaText(newValue) {
        if (this.textArea) {
            this.textArea.value = newValue;
        }
    }

    /**
     *
     */
    ngAfterViewInit(): void {
        this.focusInputSelected();
        // Evaluate initial scrollbar state after view init
        this.checkPageScroll();
        this.adjustTextareaHeight();
    }

    /**
     *
     */
    ngOnInit() {
        this.textArea = this.elementRef.nativeElement.querySelector('.input-group__textarea');
        // Listen for window resize/scroll to detect presence of a page-level vertical scrollbar
        window.addEventListener('resize', this.boundCheckPageScroll);
        window.addEventListener('scroll', this.boundCheckPageScroll);
        this.isLandingPage.set(this.computeIsLandingPage(this.router.url));
    }

    /**
     *
     * @param url
     */
    private computeIsLandingPage(url: string): boolean {
        const path = url.split(/[?#]/)[0].replace(/\/+$/, '');
        return path === '/aida';
    }

    /**
     * Pure route guard for whether the attachment icon should be shown at all.
     * Upload-in-progress state is handled separately so the icon stays visible but disabled instead of disappearing.
     */
    protected canShowAttachmentIcon(): boolean {
        return this.isLandingPage();
    }

    private boundCheckPageScroll = this.checkPageScroll.bind(this);

    /**
     *
     */
    private checkPageScroll(): void {
        const hasScroll = document.documentElement.scrollHeight > window.innerHeight;
        if (hasScroll !== this.hasPageScroll) {
            this.hasPageScroll = hasScroll;
            // Recalculate textarea height when page scroll presence changes
            if (this.textArea) {
                this.adjustTextareaHeight();
            }
            this.ref.detectChanges();
        }
    }

    /**
     *
     * @param changes
     */
    ngOnChanges(changes: SimpleChanges): void {
        if (changes?.searchValue?.currentValue) {
            const currentSearchValue = changes.searchValue.currentValue.text || '';
            if (this.textArea) {
                this.textArea.value = currentSearchValue;
                this.focusInputSelected();
            }
            this.disableSubmit = currentSearchValue.length === 0;
            this.adjustInputSize();
        }
        if (this.readOnlyInput) {
            this.placeholder =
                'This conversation is now read-only to protect your privacy. Start a new conversation to continue.';
            const input = this.inputElementRef.nativeElement;
            this.textareaText = '';
            input.blur();
            this.ref.detectChanges();
        }
    }

    /**
     *
     */
    focusInputSelected() {
        const input = this.inputElementRef.nativeElement;
        input.focus();
    }

    /**
     *
     */
    adjustInputSize() {
        if (!this.router.url.includes('chat')) {
            this.adjustTextareaHeight();
        }
    }

    /**
     *
     */
    closeFileUploadModal() {
        this.progressService.closeFileUpload();
    }

    /**
     *
     * @param event
     * @param isEnterKey
     */
    chat(event: Event) {
        if (this.textareaMaxLengthExceeded()) {
            return;
        }
        event.preventDefault();
        if (this.textArea.value) {
            // eslint-disable-next-line prefer-destructuring
            const pendo = window['pendo'];
            if (pendo && pendo.track) {
                pendo.track('User prompt text', {
                    user_input_text: this.textArea.value,
                });
            }

            this.emitChat.emit(this.textArea.value);
            this.textArea.value = '';
            this.isTyping = false;
            this.loaderService.setSendButtonDisabledFlag(true);
            this.loaderService.setAttachmentDisabledFlag(true);
            this.setTextareaRows();
            this.ref.detectChanges();
        }

        this.emitPause.emit(false);
        event.stopPropagation();
    }

    /**
     *
     */
    private textareaMaxLengthExceeded(): boolean {
        const textareaTextMaxLengthExceeded = this.textareaText.length > this.textareaMaxLength;

        if (textareaTextMaxLengthExceeded) {
            this.toastOutletService.createAlert({
                message:
                    'The entered characters are more than 10k. Please reduce the characters in the prompt or upload a document as an attachment.',
                autoDismiss: false,
            });
        }

        return textareaTextMaxLengthExceeded;
    }

    /**
     *
     * @param event
     */
    pause(event: Event) {
        this.emitPause.emit(true);
        this.isTyping = true;
        this.ref.detectChanges();
        event.stopPropagation();
    }

    /**
     *
     */
    reset() {
        this.emitReset.emit(true);
        this.emitPause.emit(false);
        // this.files = [];
        this.searchValue.text = '';
        this.valueText = '';
        this.textArea.value = '';
        this.setTextareaRows();
        this.ref.detectChanges();
    }

    /**
     *
     */
    selectFile() {
        if (this.disabled || this.progressService.attachmentDisabled()) {
            return;
        }

        // First step: consent modal. Upload chooser opens after user action.
        if (!this.progressService.isUploading()) {
            this.progressService.setUploadOrigin('conversation');
        }
        this.progressService.closeFileUpload();
        this.fileModal.open();
    }

    /**
     *
     */
    openFileUploadFromModal() {
        this.fileModal.close();
        this.progressService.openFileUpload();
    }

    /**
     * Generates a stable, unique client-side id for a newly attached file so
     * removal/matching logic never has to rely on files sharing an undefined id.
     */
    private generateFileId(): string {
        return `file_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    }

    /**
     *
     */
    handleSelectedFile() {
        // My Uploads file selections are now validated and kicked off
        // directly by UploadService.setFile() so the flow works from any
        // route, not just the ones that render this component. Nothing left
        // to do here for that origin.
        if (this.progressService.uploadOrigin === 'myUploads') {
            return;
        }

        const currentFile = this.selectedFile ? [structuredClone(this.selectedFile)] : [];

        currentFile.forEach((file: FileState) => {
            if (!file.id) {
                file.id = this.generateFileId();
            }
        });
        this.fileWarningMessage = '';
        this.files = [];
        this.fileStateService.clearFiles();
        const maxFileSizeInMB =
            this.fileUploadConfig && this.fileUploadConfig.fileMaxSize
                ? this.fileUploadConfig.fileMaxSize / 1024
                : 0;
        let maxFileSizeInMBString = maxFileSizeInMB.toString();
        let fileSize: number;
        let fileSizeString: string;
        const acceptedTypes = this.fileUploadConfig.fileTypes.split(',').map((type) => type.trim());
        const invalidFiles = currentFile.filter(
            (file) =>
                !acceptedTypes.some((type) => {
                    if (type.startsWith('.')) {
                        return file.name.endsWith(type);
                    }
                    return file.type === type;
                })
        );

        if (maxFileSizeInMBString.includes('.')) {
            maxFileSizeInMBString = maxFileSizeInMB.toFixed(2);
        }

        for (const file of currentFile) {
            if (
                this.fileUploadConfig &&
                this.fileUploadConfig.fileMaxSize &&
                file.size > this.fileUploadConfig.fileMaxSize * 1000
            ) {
                fileSize = file.size / (1024 * 1000);
                fileSizeString = fileSize.toString();

                if (fileSizeString.includes('.')) {
                    fileSizeString = fileSize.toFixed(2);
                }

                this.fileWarningMessage = `Please upload a file smaller than ${maxFileSizeInMBString}MB.<br>Your file is ${fileSizeString}MB.`;

                break;
            }
        }

        if (this.fileWarningMessage.length) {
            this.fileWarningTitle = 'File size limit reached';
            this.disableSubmit = false;
            this.progressService.closeFileUpload();
            // Clear the filename marker together with the file signal, otherwise
            // the dedup guard above (file.name === selectedFileName) stays armed
            // and silently ignores re-selecting a different file that happens to
            // share the rejected file's name.
            this.progressService.setFileName(null);
            this.progressService.setFile(null);
            this.ref.detectChanges();
            this.fileWarningModal.open();
        } else if (invalidFiles.length) {
            this.fileWarningMessage = `Only ${acceptedTypes.join(', ')} files are allowed. Uploaded file did not meet this requirement.`;
            this.fileWarningTitle = 'File type error';
            this.disableSubmit = false;
            this.progressService.closeFileUpload();
            this.progressService.setFileName(null);
            this.progressService.setFile(null);
            this.ref.detectChanges();
            this.fileWarningModal.open();
        } else {
            this.files = [...currentFile];
            this.disableSubmit = !this.textArea?.value;
            this.loaderService.setAttachmentDisabledFlag(true);
            this.progressService.closeFileUpload();
            this.fileStateService.addFiles(this.files);
            // TODO: Check if this affect others behaviour
            this.progressService.setFileName(null);
            // Clear the selected-file signal too, so a stale file doesn’t replay later
            // and overwrite a newly chosen file when the component remounts.
            this.progressService.setFile(null);
            this.filesSelected.emit(this.files);
        }
    }

    /**
     *
     */
    adjustTextareaHeight() {
        const textarea = this.textArea;
        if (textarea?.style) {
            textarea.style.height = 'auto';
            const computedStyles = window.getComputedStyle(textarea);
            const lineHeight = parseFloat(computedStyles.lineHeight) || 20;
            const maxHeight = Math.round(lineHeight * this.maxVisibleLines);
            const nextHeight = Math.min(textarea.scrollHeight, maxHeight);

            textarea.style.height = `${nextHeight}px`;
            this.textareaHasOverflow = textarea.scrollHeight > maxHeight;
        }
    }

    /**
     *
     */
    setTextareaRows() {
        this.disableSubmit = this.files?.length && !this.textArea.value.length;
        this.adjustTextareaHeight();
    }

    /**
     *
     * @param file
     */
    handleDeleteFile(file: FileState) {
        this.fileStateService.removeFile(file?.id, file?.name);
        const normalizedName = file?.name?.trim().toLowerCase();
        const files = this.files.filter((item) => {
            const itemName = item?.name?.trim().toLowerCase();
            const sameId = !!file?.id && item.id === file.id;
            const sameName = !!normalizedName && itemName === normalizedName;
            return !sameId && !sameName;
        });
        this.files = [...files];
        this.ref.markForCheck();
        this.fileStateService.setShowConversationFiles(this.files.length > 0);
        this.disableSubmit = false;
        this.filesSelected.emit(this.files);
    }

    /**
     *
     */
    onKeepWaiting() {
        this.progressService.keepWaiting();
    }

    /**
     *
     */
    onCancelUpload() {
        this.cancelUpload.emit();
        this.files = [];
        this.progressService.cancelUpload();
        this.progressService.closeFileUpload();
        this.fileStateService.clearFiles();
        this.fileStateService.clearConversationFiles();
        this.disableSubmit = !this.textArea?.value;
        this.progressService.setFileName(null);
        this.filesSelected.emit(this.files);
        this.ref.detectChanges();
    }

    /**
     *
     */
    onUploadDifferent() {
        // 1. Cancel current
        this.onCancelUpload();
        this.selectFile();
    }
}
